import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  Side,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import { Popover } from '@headlessui/react'
import { Lozenge } from '@/atoms/Lozenge'
import { ParagraphSM, TitleXXS } from '@/atoms/Text'
import { getWebClient } from '@/services/ApolloClient'
import { getGuildRatingBySlug, type GuildRating as GuildRatingData } from '@/services/GuildRatingService'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface GuildRecommendedAgeProps {
  projectSlug?: string
  overrideButtonClassName?: string
}

export const GuildRecommendedAge: React.FC<GuildRecommendedAgeProps> = ({ projectSlug, overrideButtonClassName }) => {
  const [guildRating, setGuildRating] = useState<GuildRatingData | null>(null)
  const [isHovering, setIsHovering] = useState(false)
  const { t } = useTranslate('common')
  const client = getWebClient()

  const arrowRef = React.useRef<HTMLDivElement>(null)

  const { x, y, refs, strategy, middlewareData, context } = useFloating({
    placement: 'bottom',
    middleware: [
      offset(5),
      flip({
        fallbackPlacements: ['bottom', 'top', 'bottom-start', 'bottom-end'],
      }),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef,
      }),
    ],
    whileElementsMounted: autoUpdate,
    open: isHovering,
    onOpenChange: (open) => {
      setIsHovering(open)
    },
  })

  // Handle clicks outside the tooltip
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const staticSide = useMemo(() => {
    const side = 'bottom'.split('-')[0] || ('' as Side)
    return {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    }[side] as string
  }, [])

  const fetchGuildRating = useCallback(async () => {
    if (!projectSlug) {
      setGuildRating(null)
      return
    }

    try {
      const rating = await getGuildRatingBySlug(projectSlug, client)
      setGuildRating(rating)
    } catch (error) {
      setGuildRating(null)
    }
  }, [projectSlug, client])

  useEffect(() => {
    fetchGuildRating()
  }, [fetchGuildRating])

  // Don't render if no rating data is available
  if (!guildRating) {
    return null
  }

  const age = guildRating.ageText

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            ref={refs.setReference}
            {...getReferenceProps({
              onMouseEnter: () => setIsHovering(true),
              onMouseLeave: () => setIsHovering(false),
            })}
            className="md:hover:cursor-pointer"
          >
            <Lozenge
              size="sm"
              className={
                overrideButtonClassName ??
                'md:!photon-eyebrow-sm whitespace-nowrap !bg-black/30 !text-white md:h-5 md:!rounded-[5px] md:!px-2.5 md:!py-1'
              }
              variant="transparent"
            >
              {guildRating.ageText}
            </Lozenge>
          </Popover.Button>

          {(open || isHovering) && (
            <div
              ref={refs.setFloating}
              {...getFloatingProps()}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'calc(100vw - 2rem)',
                maxWidth: '320px',
              }}
              className="z-10 rounded-2xl bg-white p-6 text-left shadow-light-2"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
            >
              <div
                ref={arrowRef}
                className="absolute h-3 w-3 rotate-45 bg-white"
                style={{
                  left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : '',
                  top: middlewareData.arrow?.y != null ? `${middlewareData.arrow.y}px` : '',
                  right: '',
                  bottom: '',
                  [staticSide]: '-5px',
                }}
              />

              <TitleXXS color="core-gray-950" weight="medium">
                <Translate i18nKey="guildRecommendedAgeTitle" t={t} values={{ age }}>
                  <strong>{{ age }}</strong> (Guild Recommended Age)
                </Translate>
              </TitleXXS>
              <ParagraphSM className="mt-2 text-[0.688rem] leading-[16px]" color="core-gray-700">
                {guildRating.description}
              </ParagraphSM>
            </div>
          )}
        </>
      )}
    </Popover>
  )
}
